import React, { useState, useEffect } from 'react';
import { 
  View, 
  Text, 
  StyleSheet, 
  TouchableOpacity, 
  ScrollView, 
  Platform 
} from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components';
import { PaymentModal, PaymentModalContent } from '@/components/residents/payments';
import ACHDetailsForm from './components/ACHDetailsForm';
import CreditCardDetailsForm from './components/CreditCardDetailsForm';
import { PaymentMethodSelector } from '@/components/residents/payments/PaymentMethodSelector';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { useLazyGetFeeStructureQuery } from '@/store/api';
import { transformFeeStructureDollarSign } from '@/utils/residents/paymentUtils';

type Params = {
  leaseId: number;
  residentId?: number;
  returnTo?: string;
  paymentType?: 'ach' | 'card';
  successPage?: string;
  managePaymentPage?: string;
};

export enum PAYMENT_METHOD_TYPE {
  NONE = 'none',
  ACH = 'ach',
  CARD = 'card',
}

export const AddOrChangePaymentMethodModal: React.FC = () => {
  const route = useRoute<any>();
  const navigation = useNavigation<any>();
  const { leaseId, residentId, returnTo, paymentType, successPage, managePaymentPage } = (route.params || {}) as Params;
  
  const [selectedType, setSelectedType] = useState<PAYMENT_METHOD_TYPE>(
    paymentType === 'ach' ? PAYMENT_METHOD_TYPE.ACH :
    paymentType === 'card' ? PAYMENT_METHOD_TYPE.CARD :
    PAYMENT_METHOD_TYPE.NONE
  );

  // Get payment data to check if user has existing methods
  const paymentData = usePaymentData(residentId);
  const { paymentMethods, selectedPaymentMethod, updateSelectedPaymentMethod } = paymentData;
  const hasValidPaymentMethods = paymentMethods.length > 0;

  // Get fee structure for display
  const [getFeeStructure, { data: feeStructure }] = useLazyGetFeeStructureQuery();
  
  useEffect(() => {
    getFeeStructure(undefined, true);
  }, [getFeeStructure]);

  const fees = {
    creditCard: feeStructure?.creditCard || '',
    debitCard: feeStructure?.debitCard || '',
    bankAccount: feeStructure?.bankAccount || '',
  };

  const handleBack = () => {
    if (selectedType !== PAYMENT_METHOD_TYPE.NONE) {
      setSelectedType(PAYMENT_METHOD_TYPE.NONE);
    } else {
      navigation.goBack();
    }
  };

  const handleSuccess = () => {
    if (successPage) {
      navigation.replace(successPage, { leaseId });
    } else if (returnTo) {
      navigation.replace(returnTo, { leaseId });
    } else {
      navigation.goBack();
    }
  };

  const handleManagePaymentMethods = () => {
    // Replace current modal with manage wallet modal
    if (managePaymentPage) {
      navigation.replace(managePaymentPage, { leaseId, returnTo });
    } else {
      navigation.replace('ManageMyWallet', { leaseId, returnTo });
    }
  };

  const onClose = () => navigation.goBack();

  const modalTitle = hasValidPaymentMethods ? "Change your payment method" : "Add a payment method";

  const renderMethodSelection = () => (
    <View style={styles.content}>
      <Text style={styles.selectionTitle}>Please choose the type of payment you wish to use</Text>
      
      <View style={styles.methodOptions}>
        <PaymentMethodOption
          icon="CREDIT_CARD"
          title="Credit or Debit Card"
          subtitle="Add a credit or debit card"
          onPress={() => setSelectedType(PAYMENT_METHOD_TYPE.CARD)}
        />
        
        <PaymentMethodOption
          icon="BANK"
          title="Bank Account (ACH)"
          subtitle="Add a checking or savings account"
          onPress={() => setSelectedType(PAYMENT_METHOD_TYPE.ACH)}
        />
      </View>

      {hasValidPaymentMethods && (
        <View style={styles.existingMethodsSection}>
          <View style={styles.existingMethodsHeader}>
            <Text style={styles.existingMethodsTitle}>Saved payment methods</Text>
            <TouchableOpacity onPress={handleManagePaymentMethods}>
              <Text style={styles.manageLink}>Manage</Text>
            </TouchableOpacity>
          </View>
          
          <PaymentMethodSelector
            paymentMethods={paymentMethods}
            selectedMethod={selectedPaymentMethod}
            onSelectMethod={updateSelectedPaymentMethod}
            onManagePaymentMethods={handleManagePaymentMethods}
            isLoading={false}
            showChangeButton={false}
            maxHeight={240}
          />
        </View>
      )}

      {!hasValidPaymentMethods && (
        <View style={styles.feesSection}>
          <Text style={styles.feesTitle}>Convenience fees</Text>
          <View style={styles.feesTable}>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Credit</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.creditCard)}</Text>
            </View>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Debit</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.debitCard)}</Text>
            </View>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>ACH</Text>
              <Text style={styles.feeValue}>{transformFeeStructureDollarSign(fees.bankAccount)}</Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );

  const renderFormContent = () => {
    switch (selectedType) {
      case PAYMENT_METHOD_TYPE.ACH:
        return (
          <ACHDetailsForm
            leaseId={leaseId}
            onSuccess={handleSuccess}
            onBack={handleBack}
            showMakeDefault={true}
            showInstructions={true}
          />
        );
      
      case PAYMENT_METHOD_TYPE.CARD:
        return (
          <CreditCardDetailsForm
            leaseId={leaseId}
            onSuccess={handleSuccess}
            onBack={handleBack}
            showMakeDefault={true}
            showInstructions={true}
          />
        );
      
      default:
        return renderMethodSelection();
    }
  };

  const getTitle = () => {
    switch (selectedType) {
      case PAYMENT_METHOD_TYPE.ACH: return 'ACH Details';
      case PAYMENT_METHOD_TYPE.CARD: return 'Credit or Debit Card Details';
      default: return modalTitle;
    }
  };

  const renderFooter = () => {
    if (selectedType !== PAYMENT_METHOD_TYPE.NONE) {
      return null; // Forms handle their own footers
    }

    return (
      <View style={styles.footer}>
        {hasValidPaymentMethods ? (
          <>
            <RNButton 
              title="Cancel" 
              variant="outlined" 
              onPress={onClose}
              style={styles.footerButton}
              textStyle={styles.footerButtonText}
            />
            <RNButton 
              title="Save" 
              variant="primary" 
              onPress={handleSuccess}
              style={styles.footerButton}
              textStyle={styles.footerButtonText}
            />
          </>
        ) : (
          <>
            <RNButton 
              title="Do it later" 
              variant="link" 
              onPress={onClose}
              style={styles.footerButton}
            />
            <RNButton 
              title="Add Now" 
              variant="primary" 
              onPress={() => {}} // Will be handled by method selection
              style={styles.footerButton}
              textStyle={styles.footerButtonText}
            />
          </>
        )}
      </View>
    );
  };

  return (
    <PaymentModal title={getTitle()} onClose={onClose} width={600}>
      <PaymentModalContent>
        <ScrollView showsVerticalScrollIndicator={false}>
          {renderFormContent()}
        </ScrollView>
      </PaymentModalContent>
      {renderFooter()}
    </PaymentModal>
  );
};

interface PaymentMethodOptionProps {
  icon: string;
  title: string;
  subtitle: string;
  onPress: () => void;
}

const PaymentMethodOption: React.FC<PaymentMethodOptionProps> = ({
  icon,
  title,
  subtitle,
  onPress,
}) => {
  const IconComponent = (RESIDENTS_SVG_COMPONENTS as any)[icon];
  
  return (
    <TouchableOpacity style={styles.methodOption} onPress={onPress}>
      <View style={styles.methodIcon}>
        {IconComponent && (
          <IconComponent 
            width={32} 
            height={32} 
            color={RESIDENTS_DESKTOP_COLORS.ICON_ACTIVE} 
          />
        )}
      </View>
      
      <View style={styles.methodContent}>
        <Text style={styles.methodTitle}>{title}</Text>
        <Text style={styles.methodSubtitle}>{subtitle}</Text>
      </View>
      
      <View style={styles.methodArrow}>
        {(() => {
          const ArrowIcon = (RESIDENTS_SVG_COMPONENTS as any).CHEVRON_RIGHT;
          return ArrowIcon ? (
            <ArrowIcon 
              width={20} 
              height={20} 
              color={RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY} 
            />
          ) : null;
        })()}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  content: {
    padding: 20,
  },
  selectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 24,
    // textAlign: 'left',
  },
  methodOptions: {
    marginBottom: 32,
  },
  methodOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    marginBottom: 12,
  },
  methodIcon: {
    marginRight: 16,
  },
  methodContent: {
    flex: 1,
  },
  methodTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 4,
  },
  methodSubtitle: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
  },
  methodArrow: {
    marginLeft: 12,
  },
  existingMethodsSection: {
    marginBottom: 32,
  },
  existingMethodsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  existingMethodsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  manageLink: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
  },
  feesSection: {
    marginBottom: 32,
  },
  feesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    marginBottom: 16,
  },
  feesTable: {
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
  },
  feeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  feeLabel: {
    fontSize: 14,
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
  feeValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
  },
  footerButton: {
    flex: 0.48,
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  footerButtonText: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
  },
});

export default AddOrChangePaymentMethodModal;
